import { makeStyles, Container, Box, Typography } from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { useTranslation } from 'next-i18next'

import T from '../../home-new/tokens'
import getStatusMeta from './trackingStatusMeta'
import TrackingDetailNew from './TrackingDetailNew'

// NEW-design (?new) tracking results — rebuilt to the tracking mockup
// (new-design-other/tracking). Each searched parcel is a collapsed summary
// row (bold blue courier tracking number + colored status pill + round blue
// chevron); clicking the chevron expands it into the full detail card
// (TrackingDetailNew). All data comes from the same tracking API items the
// old design uses; only the presentation changed. The old /tracking (no ?new)
// page keeps its own shared Steps/DetailCard/StatusCard components untouched.
//
// The tab bar (all / by-status) from the previous version is intentionally
// dropped — the mockup shows a single flat list of every searched number.
const useStyles = makeStyles((theme) => ({
  section: {
    padding: '8px 0 24px',
    fontFamily: T.font.body,
    [theme.breakpoints.down('xs')]: { padding: '4px 0 16px' },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    background: T.color.surface,
    border: `1px solid ${T.color.line}`,
    borderRadius: T.radius.lg,
    boxShadow: T.shadow.card,
    padding: '22px 28px',
    [theme.breakpoints.down('xs')]: { padding: '16px 18px', gap: '10px' },
  },
  rowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
    flexWrap: 'wrap',
    minWidth: 0,
  },
  trackingNo: {
    fontFamily: T.font.heading,
    fontWeight: 700,
    fontSize: 20,
    color: T.color.brand,
    wordBreak: 'break-all',
    [theme.breakpoints.down('xs')]: { fontSize: 16 },
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#fff',
    fontWeight: 600,
    fontSize: 15,
    lineHeight: 1,
    padding: '9px 20px',
    borderRadius: T.radius.pill,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: { fontSize: 13, padding: '8px 14px' },
  },
  chevronBtn: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: T.color.brand,
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0,158,255,0.35)',
    transition: 'transform 0.2s ease',
    '&:hover': { transform: 'translateY(-2px)' },
    [theme.breakpoints.down('xs')]: { width: 34, height: 34 },
  },
  notFoundStatus: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px 0',
  },
}))

const TrackingResultsNew = ({
  t,
  trackingData,
  open,
  handleOpen,
  trackingState,
}) => {
  const classes = useStyles()
  const { i18n } = useTranslation('tracking')
  const lang = i18n.language === 'th' ? 'th' : 'en'

  if (!trackingData) return null

  const items = trackingData.all || []

  const isExpanded = (id) => {
    if (open === id) return true
    // Auto-expand when there is a single result and the user hasn't collapsed
    // it (collapse sets open to `close-<id>`).
    if ((!open || open === '') && items.length === 1) return true
    return false
  }

  return (
    <section className={classes.section}>
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        {items.length === 0 && (
          <Box className={classes.notFoundStatus}>
            <Typography variant="h4">{t('not-found-status')}</Typography>
          </Box>
        )}
        <div className={classes.list}>
          {items.map((item) => {
            const id = item.courier_tracking_code
            const meta = getStatusMeta(item.order_status, item.delivery_type, lang)
            const expanded = isExpanded(id)

            if (expanded) {
              return (
                <TrackingDetailNew
                  key={id}
                  item={item}
                  timeline={trackingState(item)}
                  t={t}
                  lang={lang}
                  onCollapse={() => handleOpen(id, 0)}
                />
              )
            }

            return (
              <div
                key={id}
                className={classes.row}
                onClick={(e) => {
                  const top =
                    e.currentTarget.getBoundingClientRect().top +
                    window.scrollY -
                    24
                  handleOpen(id, top < 0 ? 0 : top)
                }}
                role="button"
                tabIndex={0}
              >
                <div className={classes.rowLeft}>
                  <span className={classes.trackingNo}>{id}</span>
                  <span
                    className={classes.pill}
                    style={{ backgroundColor: meta.pillBg }}
                  >
                    {meta.text}
                  </span>
                </div>
                <button
                  type="button"
                  className={classes.chevronBtn}
                  aria-label="expand"
                >
                  <KeyboardArrowDownIcon />
                </button>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}

export default TrackingResultsNew
